import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/home-video-section-two`;
const UPLOAD_API = `${ENV_API_BASE_URL}/api/upload/video`;

const DEFAULT_FORM = {
  heading: "Deepen and Enhance The Quality of Your Teachings",
  description:
    "Our program focuses on refining your techniques, understanding the subtleties of alignment, and mastering the art of effective communication.",
  videoUrl: "",
};

export default function HomeVideoSectionTwo() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Video input mode: "url" | "upload"
  const [videoMode, setVideoMode] = useState("url");

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState("");

  const fileInputRef = useRef(null);

  useEffect(() => {
    axios
      .get(API)
      .then((res) => {
        if (res.data?.data) {
          setForm({
            heading: res.data.data.heading || DEFAULT_FORM.heading,
            description: res.data.data.description || DEFAULT_FORM.description,
            videoUrl: res.data.data.videoUrl || "",
          });
          if (res.data.data.videoUrl) {
            setUploadedUrl(res.data.data.videoUrl);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load homepage video section two:", err);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Invalid file type. Please upload MP4, WebM, OGG, or MOV.");
      return;
    }
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      setUploadError("File is too large. Maximum size is 500MB.");
      return;
    }

    setUploadError("");
    setUploadFile(file);
    setUploadedUrl("");

    // Local preview
    const previewUrl = URL.createObjectURL(file);
    setUploadPreview(previewUrl);
  };

  // Upload file to server
  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError("");

    const formData = new FormData();
    formData.append("video", uploadFile);

    try {
      const res = await axios.post(UPLOAD_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        },
      });

      const uploadedVideoUrl = res.data?.url || res.data?.data?.url || "";
      setUploadedUrl(uploadedVideoUrl);
      setForm((prev) => ({ ...prev, videoUrl: uploadedVideoUrl }));
    } catch (err) {
      setUploadError(
        err.response?.data?.message || "Upload failed. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  // Remove uploaded/selected file
  const handleRemoveFile = () => {
    setUploadFile(null);
    setUploadPreview("");
    setUploadedUrl("");
    setUploadProgress(0);
    setUploadError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setForm((prev) => ({ ...prev, videoUrl: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    setError("");

    try {
      await axios.put(API, form);
      setSaved(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to save homepage video section two."
      );
    } finally {
      setLoading(false);
    }
  };

  // Determine preview URL
  const previewSrc = videoMode === "upload" ? uploadPreview || uploadedUrl : form.videoUrl;

  return (
    <div className="container pt-5 mt-5" style={{ maxWidth: 900 }}>
      <h3 className="mb-1">Homepage Video Section Two</h3>
      <p className="text-muted small mb-4">
        Edit the title, description, and video for the homepage section
        &quot;Deepen and Enhance The Quality of Your Teachings&quot;.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {saved && (
        <div className="alert alert-success">
          Homepage video section two saved successfully.
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
        {/* Heading */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Heading</label>
          <input
            type="text"
            name="heading"
            className="form-control"
            value={form.heading}
            onChange={handleChange}
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Description</label>
          <textarea
            name="description"
            className="form-control"
            rows={4}
            value={form.description}
            onChange={handleChange}
          />
        </div>

        {/* Video Section */}
        <div className="mb-4">
          <label className="form-label fw-semibold">Video</label>

          {/* Toggle Tabs */}
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className={`btn btn-sm ${videoMode === "url" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => {
                setVideoMode("url");
                setUploadError("");
              }}
            >
              🔗 Paste URL
            </button>
            <button
              type="button"
              className={`btn btn-sm ${videoMode === "upload" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => {
                setVideoMode("upload");
                setError("");
              }}
            >
              ⬆️ Upload File
            </button>
          </div>

          {/* URL Input */}
          {videoMode === "url" && (
            <input
              type="text"
              name="videoUrl"
              className="form-control"
              value={form.videoUrl}
              onChange={handleChange}
              placeholder="https://example.com/video.mp4"
            />
          )}

          {/* Upload Input */}
          {videoMode === "upload" && (
            <div>
              {/* Drop zone / file picker */}
              {!uploadFile && !uploadedUrl && (
                <div
                  className="border border-2 border-dashed rounded p-4 text-center"
                  style={{ borderColor: "#adb5bd", cursor: "pointer", background: "#f8f9fa" }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      handleFileChange({ target: { files: [file] } });
                    }
                  }}
                >
                  <div style={{ fontSize: 36 }}>🎬</div>
                  <p className="mb-1 fw-semibold">Click or drag a video file here</p>
                  <p className="text-muted small mb-0">MP4, WebM, OGG, MOV — max 500MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {/* File selected but not yet uploaded */}
              {uploadFile && !uploadedUrl && (
                <div className="border rounded p-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <span className="fw-semibold">{uploadFile.name}</span>{" "}
                      <span className="text-muted small">
                        ({(uploadFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleRemoveFile}
                    >
                      Remove
                    </button>
                  </div>

                  {/* Progress bar */}
                  {uploading && (
                    <div className="progress mb-2" style={{ height: 6 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}

                  {uploadError && (
                    <div className="alert alert-danger py-2 mb-2 small">{uploadError}</div>
                  )}

                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? `Uploading… ${uploadProgress}%` : "Upload Video"}
                  </button>
                </div>
              )}

              {/* Upload success */}
              {uploadedUrl && (
                <div className="border rounded p-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-success fw-semibold">✅ Video uploaded</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleRemoveFile}
                    >
                      Replace
                    </button>
                  </div>
                  <div className="text-muted small text-truncate mb-0" title={uploadedUrl}>
                    {uploadedUrl}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Video Preview */}
          {previewSrc && (
            <div className="mt-3">
              <label className="form-label text-muted small">Preview</label>
              <video
                key={previewSrc}
                src={previewSrc}
                controls
                className="w-100 rounded"
                style={{ maxHeight: 300, background: "#000" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : "Save Video Section Two"}
          </button>
        </div>
      </form>
    </div>
  );
}